/**
 * desktopWindow — who owns the window frame right now.
 *
 * The desktop app draws its own title bar (`decorations: false` in
 * `tauri.windows.conf.json` / `tauri.linux.conf.json`), so the app header
 * doubles as the drag strip and carries its own minimise/maximise/close.
 *
 * macOS is the deliberate exception: `tauri.macos.conf.json` keeps decorations
 * and sets `titleBarStyle: "Overlay"`, so the REAL traffic lights float over our
 * content. Drawing fake ones there would be wrong, and obviously wrong to
 * anyone who uses a Mac. The layout still insets for them — see
 * `--titlebar-inset-start` in Header.css.
 *
 * Platform is read off the user agent rather than `@tauri-apps/plugin-os`
 * because that plugin is not a dependency here and this is the only thing that
 * needs the answer.
 */

/** True inside the Tauri webview. The web build must never see window chrome. */
export function isTauri() {
    return typeof window !== 'undefined' && !!window.__TAURI_INTERNALS__;
}

/** True on macOS (or an Apple mobile webview, which is the same answer here). */
export function isMacOS() {
    if (typeof navigator === 'undefined') return false;
    return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent || '');
}

/** True only where WE are responsible for drawing the window buttons. */
export function usesCustomWindowControls() {
    return isTauri() && !isMacOS();
}

/** True where the layout must leave room for macOS's overlaid traffic lights. */
export function needsTrafficLightInset() {
    return isTauri() && isMacOS();
}

/**
 * The Tauri window handle for the current webview.
 *
 * The single place that knows the `@tauri-apps/api` import path — the codebase
 * imports Tauri APIs lazily so the web bundle never pulls them, and having each
 * caller repeat that import spreads a hard-to-test seam around. Callers must
 * check `isTauri()` first; outside the webview this rejects, because
 * `getCurrentWindow()` reads `__TAURI_INTERNALS__.metadata` and there is no
 * meaningful window to hand back.
 */
export async function getDesktopWindow() {
    const { getCurrentWindow } = await import('@tauri-apps/api/window');
    return getCurrentWindow();
}
